import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useImages } from '../context/ImageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase/supabase';

const AdminPortal = () => {
  const { images, updateHero, updateGallery, resetToDefault, loading: imagesLoading } = useImages();
  const [activeTab, setActiveTab] = useState('images');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (activeTab === 'leads') fetchLeads();
  }, [activeTab, showArchived]);

  const fetchLeads = async () => {
    setLeadsLoading(true);
    let query = supabase
      .from('contact_inquiries_1740051000000')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (!showArchived) {
      query = query.eq('is_archived', false);
    }

    const { data, error } = await query;
    if (error) console.error("Fetch error:", error);
    setLeads(data || []);
    setLeadsLoading(false);
  };

  const updateLead = async (id, updates) => {
    const { error } = await supabase
      .from('contact_inquiries_1740051000000')
      .update(updates)
      .eq('id', id);

    if (!error) {
      // If we archived it and we aren't showing archived, remove from list
      if (updates.is_archived !== undefined && !showArchived && updates.is_archived === true) {
        setLeads(leads.filter(l => l.id !== id));
      } else {
        setLeads(leads.map(l => l.id === id ? { ...l, ...updates } : l));
      }
      
      if (selectedLead?.id === id) {
        setSelectedLead(prev => ({ ...prev, ...updates }));
      }
      return true;
    }
    console.error("Update error:", error);
    return false;
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this inquiry?")) return;
    
    const { error } = await supabase
      .from('contact_inquiries_1740051000000')
      .delete()
      .eq('id', id);

    if (!error) {
      setLeads(leads.filter(l => l.id !== id));
      setSelectedLead(null);
    } else {
      console.error("Delete error:", error);
    }
  };

  if (imagesLoading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-parlor-cream py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
          <div>
            <h1 className="font-serif text-4xl text-parlor-green">Management Suite</h1>
            <div className="flex gap-4 mt-4">
              <TabButton active={activeTab === 'images'} onClick={() => setActiveTab('images')} icon={FiIcons.FiImage} label="Site Content" />
              <TabButton active={activeTab === 'leads'} onClick={() => setActiveTab('leads')} icon={FiIcons.FiUsers} label="Inquiries" />
            </div>
          </div>
          <Link to="/" className="bg-parlor-green text-parlor-cream px-6 py-3 uppercase tracking-widest text-xs font-bold hover:bg-parlor-gold transition-colors flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiEye} /> View Site
          </Link>
        </header>

        {activeTab === 'images' ? (
          <ImageManager images={images} updateHero={updateHero} updateGallery={updateGallery} resetToDefault={resetToDefault} />
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-4 border border-parlor-green/10">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="archivedToggle" 
                  checked={showArchived} 
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-4 h-4 accent-parlor-gold"
                />
                <label htmlFor="archivedToggle" className="text-xs uppercase tracking-widest font-bold text-parlor-green/60 cursor-pointer">
                  Show Archived Inquiries
                </label>
              </div>
              <button onClick={fetchLeads} className="text-parlor-gold hover:text-parlor-green transition-colors">
                <SafeIcon icon={FiIcons.FiRefreshCw} className={leadsLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            <LeadsManager 
              leads={leads} 
              loading={leadsLoading} 
              onSelectLead={setSelectedLead}
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedLead && (
          <LeadDetailsModal 
            lead={selectedLead} 
            onClose={() => setSelectedLead(null)} 
            onUpdate={updateLead}
            onDelete={deleteLead}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all border-b-2 ${active ? 'border-parlor-gold text-parlor-green' : 'border-transparent text-parlor-green/40'}`}>
    <SafeIcon icon={icon} /> {label}
  </button>
);

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-parlor-cream">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-parlor-gold"></div>
  </div>
);

const ImageManager = ({ images, updateHero, updateGallery, resetToDefault }) => (
  <div className="grid gap-8">
    <div className="bg-white p-8 border border-parlor-green/10 shadow-sm">
      <h2 className="font-serif text-2xl text-parlor-green mb-6 flex items-center gap-3">
        <SafeIcon icon={FiIcons.FiImage} className="text-parlor-gold" /> Hero Background
      </h2>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <input type="text" value={images.hero} onChange={(e) => updateHero(e.target.value)} className="w-full bg-parlor-cream border border-parlor-green/10 p-4 text-sm" />
          <p className="text-xs text-parlor-green/50 italic">Updates instantly on the live site.</p>
        </div>
        <img src={images.hero} className="aspect-video object-cover border border-parlor-green/5" alt="Hero Preview" />
      </div>
    </div>
    <div className="bg-white p-8 border border-parlor-green/10 shadow-sm">
      <h2 className="font-serif text-2xl text-parlor-green mb-8">Gallery Magnets</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
        {images.gallery.map((item, idx) => (
          <div key={idx} className="space-y-3">
            <div className="aspect-square bg-parlor-cream border border-parlor-green/10 overflow-hidden">
              <img src={item.url} className="w-full h-full object-cover" alt={`Gallery ${idx}`} />
            </div>
            <input type="text" value={item.url} onChange={(e) => updateGallery(idx, e.target.value)} className="w-full bg-parlor-cream border border-parlor-green/10 p-2 text-[10px]" />
          </div>
        ))}
      </div>
    </div>
    <button onClick={resetToDefault} className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-600 self-start">Reset to Artisan Defaults</button>
  </div>
);

const LeadsManager = ({ leads, loading, onSelectLead }) => (
  <div className="bg-white border border-parlor-green/10 shadow-sm overflow-x-auto">
    <table className="w-full text-left min-w-[1000px]">
      <thead className="bg-parlor-cream border-b border-parlor-green/10">
        <tr>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Date</th>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Status</th>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Contact</th>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Event Info</th>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Message</th>
          <th className="p-4 text-[10px] uppercase tracking-widest text-parlor-green/60">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-parlor-green/5">
        {loading ? (
          <tr><td colSpan="6" className="p-12 text-center text-parlor-green/40">Loading inquiries...</td></tr>
        ) : leads.length === 0 ? (
          <tr><td colSpan="6" className="p-12 text-center text-parlor-green/40">No inquiries found.</td></tr>
        ) : (
          leads.map((lead) => (
            <tr key={lead.id} className={`hover:bg-parlor-cream/30 transition-colors group ${lead.is_archived ? 'opacity-50' : ''}`}>
              <td className="p-4 text-[10px]">{new Date(lead.created_at).toLocaleDateString()}</td>
              <td className="p-4">
                <StatusBadge status={lead.status} />
                {lead.is_archived && <span className="ml-2 text-[8px] uppercase font-bold text-gray-400 border border-gray-200 px-1 rounded">Archived</span>}
              </td>
              <td className="p-4">
                <div className="text-xs font-bold text-parlor-green">{lead.first_name} {lead.last_name}</div>
                <div className="text-[10px] text-parlor-green/60">{lead.email}</div>
                <div className="text-[10px] text-parlor-green/60">{lead.phone}</div>
              </td>
              <td className="p-4">
                <div className="text-[10px] font-bold text-parlor-gold uppercase tracking-wider">{lead.event_type}</div>
                <div className="text-[10px] text-parlor-green/60">{lead.event_date || 'TBD'}</div>
              </td>
              <td className="p-4 text-xs text-parlor-green/70 max-w-[200px] truncate">{lead.message}</td>
              <td className="p-4">
                <button 
                  onClick={() => onSelectLead(lead)}
                  className="text-[10px] uppercase tracking-widest font-bold text-parlor-gold hover:text-parlor-green transition-colors"
                >
                  Manage
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    new: 'bg-blue-50 text-blue-600 border-blue-100',
    contacted: 'bg-yellow-50 text-yellow-600 border-yellow-100',
    booked: 'bg-green-50 text-green-600 border-green-100',
    cancelled: 'bg-red-50 text-red-600 border-red-100'
  };
  return (
    <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-1 border rounded-full ${styles[status] || styles.new}`}>
      {status || 'new'}
    </span>
  );
};

const LeadDetailsModal = ({ lead, onClose, onUpdate, onDelete }) => {
  const [comments, setComments] = useState(lead.admin_comments || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSaveComments = async () => {
    setIsSaving(true);
    const success = await onUpdate(lead.id, { admin_comments: comments });
    setIsSaving(false);
    if (success) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    }
  };

  const handleStatusChange = async (newStatus) => {
    await onUpdate(lead.id, { status: newStatus });
  };

  const handleToggleArchive = async () => {
    await onUpdate(lead.id, { is_archived: !lead.is_archived });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-parlor-green/80 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white w-full max-w-2xl p-8 md:p-10 shadow-2xl border border-parlor-gold/20 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-parlor-green/40 hover:text-parlor-green"><SafeIcon icon={FiIcons.FiX} className="text-2xl" /></button>
        
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <StatusBadge status={lead.status} />
            {lead.is_archived && <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 border rounded-full bg-gray-100 text-gray-500 border-gray-200">Archived</span>}
          </div>
          <h2 className="font-serif text-3xl text-parlor-green mt-4">{lead.first_name} {lead.last_name}</h2>
          <p className="text-parlor-gold uppercase tracking-[0.2em] text-[10px] font-bold mt-1">{lead.event_type} — {lead.event_date || 'Date TBD'}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8 border-t border-b border-parlor-green/5 py-8">
          <div>
            <h4 className="text-[10px] uppercase tracking-widest text-parlor-green/40 font-bold mb-3">Contact Information</h4>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiMail} className="text-parlor-gold" /> {lead.email}</p>
              <p className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiPhone} className="text-parlor-gold" /> {lead.phone}</p>
            </div>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-widest text-parlor-green/40 font-bold mb-3">Inquiry Message</h4>
            <p className="text-sm text-parlor-green/80 italic leading-relaxed">"{lead.message}"</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-parlor-green/40 font-bold mb-3">Update Status</label>
            <div className="flex flex-wrap gap-2">
              {['new', 'contacted', 'booked', 'cancelled'].map(s => (
                <button 
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border transition-all ${lead.status === s ? 'bg-parlor-green text-white border-parlor-green' : 'border-parlor-green/10 hover:border-parlor-gold'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-parlor-green/40 font-bold mb-3">Internal Admin Comments</label>
            <textarea 
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full bg-parlor-cream border border-parlor-green/10 p-4 text-sm min-h-[100px] focus:outline-none focus:border-parlor-gold"
              placeholder="Add internal notes here..."
            />
            <div className="flex items-center gap-4 mt-2">
              <button 
                onClick={handleSaveComments}
                disabled={isSaving}
                className={`bg-parlor-gold text-parlor-green text-[10px] uppercase tracking-[0.2em] font-bold px-6 py-2 hover:bg-parlor-green hover:text-white transition-colors disabled:opacity-50`}
              >
                {isSaving ? 'Saving...' : 'Save Comments'}
              </button>
              {saveSuccess && (
                <span className="text-green-600 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                  <SafeIcon icon={FiIcons.FiCheck} /> Saved successfully
                </span>
              )}
            </div>
          </div>

          <div className="pt-6 border-t border-parlor-green/5 flex justify-between items-center">
            <button 
              onClick={handleToggleArchive}
              className="text-[10px] uppercase tracking-widest font-bold text-parlor-green/60 hover:text-parlor-green flex items-center gap-2"
            >
              <SafeIcon icon={lead.is_archived ? FiIcons.FiInbox : FiIcons.FiArchive} />
              {lead.is_archived ? 'Restore to Active' : 'Archive Inquiry'}
            </button>
            <button 
              onClick={() => onDelete(lead.id)}
              className="text-[10px] uppercase tracking-widest font-bold text-red-400 hover:text-red-600 flex items-center gap-2"
            >
              <SafeIcon icon={FiIcons.FiTrash2} />
              Delete Permanently
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminPortal;